import { NavLink } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { ChartCard } from "../components/ChartCard";
import { ChartTooltipContent } from "../components/ChartTooltipContent";
import { GlassCard } from "../components/GlassCard";
import { MetricCard } from "../components/MetricCard";
import { SignalPill } from "../components/SignalPill";
import { SignalStrip } from "../components/SignalStrip";
import { StatusBadge } from "../components/StatusBadge";
import { SummaryCard } from "../components/SummaryCard";
import { useContent } from "../hooks/useContent";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { PageContainer } from "../layout/PageContainer";
import { api, type CompsHubData } from "../lib/api";
import {
  COMP_IMAGE_ASSETS,
  COMP_IMAGE_INTEGRATION_RECOMMENDATIONS,
  COMP_IMAGE_LIMITATIONS
} from "../lib/compsImageAssets";
import { getMotionClass, getStaggerStyle } from "../lib/motion";

type Tone = "positive" | "warning" | "neutral";
type ReferenceRole = "Concept Fit" | "Functional" | "Demand Signal" | "Positioning" | "Reference";

const COMP_COLORS = {
  positive: "#76f7c6",
  warning: "#ffcf7a",
  neutral: "#65d9ff",
  accent: "#a1f4ff",
  grid: "rgba(141, 161, 199, 0.16)",
  ticks: "#8da1c7"
} as const;

function toSentence(text: string): string {
  const trimmed = text.trim();
  const firstSentence = trimmed.match(/.*?[.?!](?:\s|$)/);
  return firstSentence ? firstSentence[0].trim() : trimmed;
}

function uniqueItems(items: string[]): string[] {
  const seen = new Set<string>();

  return items.filter((item) => {
    const normalized = item.trim();
    if (!normalized) {
      return false;
    }

    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function confidenceToScore(confidence: string): number {
  const text = confidence.toLowerCase();

  if (text.includes("high")) {
    return 4;
  }

  if (text.includes("medium to high")) {
    return 3;
  }

  if (text.includes("medium")) {
    return 2;
  }

  if (text.includes("low")) {
    return 1;
  }

  return 2;
}

function getEvidenceTone(text: string): Tone {
  const normalized = text.toLowerCase();

  if (normalized.includes("not yet")) {
    return "warning";
  }

  if (normalized.includes("supported")) {
    return "positive";
  }

  return "neutral";
}

function getStatusTone(text: string): Tone {
  const normalized = text.toLowerCase();

  if (/(proceed cautiously|open|not yet|directional|live)/.test(normalized)) {
    return "warning";
  }

  if (/(supportive|final|complete|strong)/.test(normalized)) {
    return "positive";
  }

  return "neutral";
}

function getProductFamily(productType: string): string {
  const normalized = productType.toLowerCase();

  if (normalized.includes("loft")) {
    return "Downtown loft";
  }

  if (normalized.includes("studio") || normalized.includes("apartment")) {
    return "Compact urban unit";
  }

  if (normalized.includes("home") || normalized.includes("house")) {
    return "Compact home";
  }

  if (normalized.includes("experiential")) {
    return "Experiential stay";
  }

  return "Reference set";
}

function listMentionsComp(list: string[], compName: string): boolean {
  const normalized = compName.toLowerCase();
  return list.some((item) => item.toLowerCase().includes(normalized));
}

function getReferenceRole(data: CompsHubData, compName: string): ReferenceRole {
  if (compName.toLowerCase().includes("overlook")) {
    return "Concept Fit";
  }

  if (listMentionsComp(data.compsTable.strongestFunctionalComps, compName)) {
    return "Functional";
  }

  if (listMentionsComp(data.compsTable.strongestDemandSignalComps, compName)) {
    return "Demand Signal";
  }

  if (listMentionsComp(data.compsTable.secondaryPositioningComps, compName)) {
    return "Positioning";
  }

  return "Reference";
}

function getRoleTone(role: ReferenceRole): Tone {
  switch (role) {
    case "Concept Fit":
    case "Functional":
      return "positive";
    case "Demand Signal":
      return "neutral";
    case "Positioning":
      return "warning";
    default:
      return "neutral";
  }
}

function buildEvidenceChartData(data: CompsHubData) {
  return data.compsSummary.supports.map((item) => ({
    topic: item.topic,
    score: confidenceToScore(item.confidence),
    confidence: item.confidence,
    conclusion: item.conclusion,
    tone: getEvidenceTone(item.conclusion)
  }));
}

function buildCompFamilyChartData(data: CompsHubData) {
  const counts = new Map<string, number>();

  for (const item of data.compsTable.referenceSet) {
    const key = getProductFamily(item.productType);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([family, count]) => ({ family, count }))
    .sort((left, right) => right.count - left.count);
}

function buildHeroHighlights(data: CompsHubData) {
  const strongestConceptFit =
    data.compsSummary.strongestComps.find((item) => item.includes("Overlook")) ?? data.compsSummary.strongestComps[0];

  return [
    {
      label: "Lead concept",
      value: data.conceptSummary.leadConcept.concept
    },
    {
      label: "Strongest concept-fit comp",
      value: strongestConceptFit.split(":")[0] ?? strongestConceptFit
    },
    {
      label: "Micro-area fallback proof",
      value: "100 / 104 / 110 F St sales keep the downside story grounded"
    }
  ];
}

function renderReference(ref: string) {
  if (/^https?:\/\//i.test(ref)) {
    return (
      <a href={ref} target="_blank" rel="noreferrer" className="comps-source-link">
        {ref}
      </a>
    );
  }

  return <span className="comps-source-ref">{ref}</span>;
}

export function CompsPage() {
  const reducedMotion = useReducedMotion();
  const { data, loading, error } = useContent("comps-page", api.getCompsHub);

  if (!data) {
    return (
      <PageContainer
        eyebrow="Supporting Detail - Market Support"
        title="Comparable support detail for the Market Support surface"
        lede="This route is the comps-heavy supporting layer beneath Market Support. It keeps the deeper comparable-set, precedent, and evidence-work visible without functioning as a competing top-level investor surface."
      >
        {loading ? <div className="loading-panel shimmer-card">Loading comp intelligence...</div> : null}
        {error ? <div className="error-panel">{error}</div> : null}
      </PageContainer>
    );
  }

  const evidenceChartData = buildEvidenceChartData(data);
  const compFamilyChartData = buildCompFamilyChartData(data);
  const supportiveTopics = evidenceChartData.filter((item) => item.tone === "positive").length;
  const unresolvedTopics = evidenceChartData.filter((item) => item.tone !== "positive").length;
  const heroHighlights = buildHeroHighlights(data);
  const topLineSignals = [
    {
      label: "Reference set",
      value: `${data.compsTable.referenceSet.length} live comps`,
      note: "Broader Lynchburg STR signals already in publish",
      tone: "positive" as const
    },
    {
      label: "Micro-area precedent",
      value: `${data.precedentSummary.referenceProperties.length} nearby sales`,
      note: "F St makes compact-product realism local",
      tone: "positive" as const
    },
    {
      label: "Lead concept",
      value: data.conceptSummary.leadConcept.concept,
      note: "Best current fit under the evidence stack",
      tone: "positive" as const
    },
    {
      label: "Demand posture",
      value: "Supportive",
      note: toSentence(data.demandSummary.currentDemandRead),
      tone: "positive" as const
    },
    {
      label: "Current recommendation",
      value: data.memoSummary.recommendation,
      note: "Still disciplined, not promotional",
      tone: "warning" as const
    },
    {
      label: "ADR proof",
      value: "Not yet proven",
      note: "The page keeps underwriting boundaries visible",
      tone: "warning" as const
    }
  ];

  const rosterCards = data.compsTable.referenceSet.map((item) => {
    const role = getReferenceRole(data, item.compName);

    return {
      ...item,
      role,
      tone: getRoleTone(role),
      family: getProductFamily(item.productType)
    };
  });

  const compSignalCounts = uniqueItems([
    ...data.compsSummary.strongestComps,
    ...data.compsTable.strongestFunctionalComps,
    ...data.compsTable.strongestDemandSignalComps
  ]).length;

  return (
    <PageContainer
      eyebrow="Supporting Detail - Market Support"
      title="Comparable support detail for the Market Support surface"
      lede="This route is the comps-heavy supporting layer beneath Market Support. It keeps the deeper comparable-set, precedent, and evidence-work visible without functioning as a competing top-level investor surface."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading comp intelligence...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      <div className="stack-xl">
        <div className={getMotionClass(reducedMotion, "fade-up")}>
          <SignalStrip items={topLineSignals} />
        </div>

        <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(1, reducedMotion)}>
          <GlassCard className="hero-card comps-command-card stack-lg">
            <div className="hero-grid comps-command-grid">
              <div className="stack-md">
                <span className="hero-kicker">Market Support Detail</span>
                <div className="row-wrap gap-sm">
                  <StatusBadge label={data.memoSummary.recommendation} tone="warning" />
                  <StatusBadge label="R2-backed evidence hub" tone="positive" />
                  <StatusBadge label="Published final" tone={getStatusTone(data.compsSummary.status)} />
                </div>
                <h2 className="comps-command-title">The comp story now supports the parent Market Support surface with a more concrete commercial read.</h2>
                <p className="comps-command-copy strong-copy">{toSentence(data.memoSummary.summary)}</p>
                <p className="comps-command-copy">{data.compsSummary.overview}</p>

                <div className="comps-highlight-list">
                  {heroHighlights.map((item) => (
                    <div key={item.label} className="comps-highlight-item">
                      <span className="comps-highlight-label">{item.label}</span>
                      <strong className="comps-highlight-value">{item.value}</strong>
                    </div>
                  ))}
                </div>
              </div>

              <div className="hero-panel comps-command-panel stack-md">
                <div className="stack-xs">
                  <span className="card-eyebrow">Lead concept</span>
                  <div className="hero-panel-title comps-command-panel-title">{data.conceptSummary.leadConcept.concept}</div>
                  <p className="hero-panel-copy">{data.conceptSummary.leadConcept.corePositioning}</p>
                </div>
                <div className="stack-xs">
                  <span className="card-eyebrow">Current comp support</span>
                  <p className="hero-panel-copy">{data.conceptSummary.leadConcept.currentCompSupport}</p>
                </div>
                <div className="row-wrap gap-sm">
                  <SignalPill label={`${supportiveTopics} supported topics`} tone="positive" />
                  <SignalPill label={`${data.precedentSummary.referenceProperties.length} F St references`} tone="positive" />
                  <SignalPill label={`${unresolvedTopics} boundaries still visible`} tone="warning" />
                </div>
                <div className="row-wrap gap-sm overview-go-deeper-actions">
                  <NavLink to="/market-support" className="link-button link-button-secondary">Review Market Support</NavLink>
                  <NavLink to="/precedent" className="link-button link-button-secondary">Open Precedent</NavLink>
                  <NavLink to="/underwriting" className="link-button link-button-secondary">Open Underwriting</NavLink>
                  <NavLink to="/memo" className="link-button link-button-primary">Read Memo</NavLink>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>

        <div className="metrics-grid">
          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(2, reducedMotion)}>
            <MetricCard
              title="Strongest signals"
              value={`${compSignalCounts}`}
              detail="Unique promoted comp signals now supporting concept work."
            />
          </div>
          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(3, reducedMotion)}>
            <MetricCard
              title="Reference set"
              value={`${data.compsTable.referenceSet.length}`}
              detail="Processed STR rows retained in the current publish bundle."
            />
          </div>
          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(4, reducedMotion)}>
            <MetricCard
              title="Nearby precedent sales"
              value={`${data.precedentSummary.referenceProperties.length}`}
              detail="Hyper-local F St transactions keeping product realism grounded."
            />
          </div>
          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(5, reducedMotion)}>
            <MetricCard
              title="Published evidence feeds"
              value={`${data.sourcesSummary.sectionCoverage.length}`}
              detail="Sections represented in the public source summary."
            />
          </div>
        </div>

        <div className="grid-two">
          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(6, reducedMotion)}>
            <ChartCard
              eyebrow="Support Confidence"
              title="The comp set is strong on product realism and still disciplined on revenue proof"
              description="This chart is derived directly from the published support topics and confidence labels in the current R2 comps summary."
            >
              <div className="chart-area chart-area-md">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={evidenceChartData} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                    <CartesianGrid horizontal={false} stroke={COMP_COLORS.grid} />
                    <XAxis
                      type="number"
                      domain={[0, 4]}
                      ticks={[1, 2, 3, 4]}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: COMP_COLORS.ticks, fontSize: 12 }}
                    />
                    <YAxis
                      type="category"
                      dataKey="topic"
                      width={130}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: COMP_COLORS.ticks, fontSize: 12 }}
                    />
                    <Tooltip cursor={{ fill: "rgba(101, 217, 255, 0.05)" }} content={<ChartTooltipContent title="Comp support topic" />} />
                    <Bar dataKey="score" name="Confidence score" radius={[0, 8, 8, 0]}>
                      {evidenceChartData.map((entry) => (
                        <Cell
                          key={entry.topic}
                          fill={
                            entry.tone === "positive"
                              ? COMP_COLORS.positive
                              : entry.tone === "warning"
                                ? COMP_COLORS.warning
                                : COMP_COLORS.neutral
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="chart-legend">
                <div className="chart-legend-item">
                  <span className="chart-legend-swatch" style={{ backgroundColor: COMP_COLORS.positive }} />
                  Supported
                </div>
                <div className="chart-legend-item">
                  <span className="chart-legend-swatch" style={{ backgroundColor: COMP_COLORS.warning }} />
                  Not yet proven
                </div>
                <div className="chart-legend-item">
                  <span className="chart-legend-swatch" style={{ backgroundColor: COMP_COLORS.neutral }} />
                  Directional
                </div>
              </div>
            </ChartCard>
          </div>

          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(7, reducedMotion)}>
            <ChartCard
              eyebrow="Reference Mix"
              title="The comp roster is diversified, but one compact-home comp still does the most concept work"
              description="Counts are grouped from the processed comp table by product family so the composition of the reference set is legible at a glance."
            >
              <div className="chart-area chart-area-md">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={compFamilyChartData} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                    <CartesianGrid horizontal={false} stroke={COMP_COLORS.grid} />
                    <XAxis type="number" allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: COMP_COLORS.ticks, fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="family"
                      width={132}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: COMP_COLORS.ticks, fontSize: 12 }}
                    />
                    <Tooltip cursor={{ fill: "rgba(161, 244, 255, 0.05)" }} content={<ChartTooltipContent title="Reference mix" valueSuffix=" comps" />} />
                    <Bar dataKey="count" name="Comp count" radius={[0, 8, 8, 0]}>
                      {compFamilyChartData.map((entry, index) => {
                        const palette = [COMP_COLORS.accent, COMP_COLORS.positive, COMP_COLORS.neutral, COMP_COLORS.warning];
                        return <Cell key={entry.family} fill={palette[index % palette.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="chart-footnote">The Overlook remains the most important direct concept-fit comp even though the roster includes multiple families of support.</p>
            </ChartCard>
          </div>
        </div>

        <div className="grid-two">
          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(8, reducedMotion)}>
            <SummaryCard
              eyebrow="What the Market Now Supports"
              title="The market case is no longer generic"
              description={data.compsSummary.currentCompRead}
            >
              <ul className="bullet-list">
                {data.compsSummary.strongestComps.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>

          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(9, reducedMotion)}>
            <SummaryCard
              eyebrow="What the Page Refuses to Claim"
              title="Underwriting boundaries stay visible"
              description={data.underwritingSummary.currentBestRead}
            >
              <ul className="bullet-list">
                {data.underwritingSummary.limits.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>
        </div>

        <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(10, reducedMotion)}>
          <SummaryCard
            eyebrow="STR Reference Set"
            title="The broader Lynchburg roster is telling a cleaner story now"
            description={data.compsTable.earlyInterpretation}
          >
            <div className="comps-roster-grid">
              {rosterCards.map((item) => (
                <GlassCard key={item.compName} className="comps-roster-card stack-sm">
                  <div className="comps-roster-header">
                    <span className="card-eyebrow">{item.family}</span>
                    <StatusBadge label={item.role} tone={item.tone} />
                  </div>
                  <div className="stack-xs">
                    <h3 className="card-title">{item.compName}</h3>
                    <p className="card-copy">{item.notableSignal}</p>
                  </div>
                  <div className="status-meta-list">
                    <div className="status-meta-row">
                      <span className="status-meta-label">Product</span>
                      <strong className="status-meta-value">{item.productType}</strong>
                    </div>
                    <div className="status-meta-row">
                      <span className="status-meta-label">Capacity</span>
                      <strong className="status-meta-value">{item.capacitySignal}</strong>
                    </div>
                    <div className="status-meta-row">
                      <span className="status-meta-label">Location logic</span>
                      <strong className="status-meta-value">{item.locationLogic}</strong>
                    </div>
                    <div className="status-meta-row">
                      <span className="status-meta-label">Relevance</span>
                      <strong className="status-meta-value">{item.relevance}</strong>
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>
          </SummaryCard>
        </div>

        <div className="grid-two">
          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(11, reducedMotion)}>
            <SummaryCard
              eyebrow="Micro-Area Proof"
              title="F Street takes the concept from plausible to believable"
              description={data.precedentSummary.overview}
            >
              <div className="comps-precedent-property-grid">
                {data.precedentSummary.referenceProperties.map((item) => (
                  <div key={item.address} className="comps-precedent-property">
                    <span className="card-eyebrow">{item.address}</span>
                    <strong className="state-metric-value">{item.saleSignal}</strong>
                    <p className="card-copy">{item.keyImplication}</p>
                    <div className="row-wrap gap-sm">
                      <SignalPill label={item.size} tone="neutral" />
                      <SignalPill label={item.bedroomsBaths} tone="neutral" />
                    </div>
                  </div>
                ))}
              </div>
              <ul className="bullet-list">
                {data.precedentSummary.takeaways.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>

          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(12, reducedMotion)}>
            <GlassCard className="stack-md">
              <div className="stack-xs">
                <span className="card-eyebrow">Website-ready imagery</span>
                <h3 className="card-title">Comp images are ready for evidence use, not hero use</h3>
                <p className="card-copy">
                  The street views do the proof work. The maps handle proximity and clustering. The page uses locally cached assets so the client bundle does not depend on third-party map URLs.
                </p>
              </div>

              <div className="comps-asset-grid">
                {COMP_IMAGE_ASSETS.map((asset) => (
                  <article key={asset.id} className="comps-asset-card">
                    <div className="comps-asset-media">
                      <img src={asset.imagePath} alt={asset.altText} loading="lazy" />
                    </div>
                    <div className="stack-xs">
                      <div className="comps-roster-header">
                        <span className="card-eyebrow">{asset.sourceType}</span>
                        <StatusBadge label={asset.priority} tone={asset.priority === "Primary" ? "positive" : "neutral"} />
                      </div>
                      <h3 className="card-title">{asset.sourceProperty}</h3>
                      <p className="card-copy">{asset.caption}</p>
                      <p className="comps-asset-caption">{asset.notes}</p>
                    </div>
                  </article>
                ))}
              </div>

              <div className="comps-note-block">
                <span className="card-eyebrow">Integration guidance</span>
                <ul className="bullet-list">
                  {COMP_IMAGE_INTEGRATION_RECOMMENDATIONS.map((item) => (
                    <li key={item} className="bullet-item">{item}</li>
                  ))}
                  {COMP_IMAGE_LIMITATIONS.map((item) => (
                    <li key={item} className="bullet-item">{item}</li>
                  ))}
                </ul>
              </div>
            </GlassCard>
          </div>
        </div>

        <div className="grid-two">
          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(13, reducedMotion)}>
            <SummaryCard
              eyebrow="Why the Lead Concept Wins"
              title="The comp stack points to one cleaner product thesis"
              description={data.conceptSummary.currentRecommendationRead}
            >
              <ul className="bullet-list">
                {data.conceptSummary.whyLead.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>

          <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(14, reducedMotion)}>
            <SummaryCard
              eyebrow="Demand Anchors"
              title="The comp page still sits on a real location thesis"
              description={data.demandSummary.locationOverview}
            >
              <ul className="bullet-list">
                {data.demandSummary.demandDrivers.map((item) => (
                  <li key={item.demandDriver} className="bullet-item">{`${item.demandDriver}: ${item.whyItMatters}`}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>
        </div>

        <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(15, reducedMotion)}>
          <SummaryCard
            eyebrow="Source Provenance"
            title="The public evidence layer still shows its homework"
            description={data.sourcesSummary.summary}
          >
            <div className="comps-source-grid">
              {data.sourcesSummary.representativeSources.slice(0, 6).map((item) => (
                <div key={`${item.name}-${item.reference}`} className="comps-source-card">
                  <div className="comps-roster-header">
                    <span className="card-eyebrow">{item.type}</span>
                    <StatusBadge label={item.reliability} tone={item.reliability.toLowerCase() === "high" ? "positive" : "neutral"} />
                  </div>
                  <div className="stack-xs">
                    <strong className="state-metric-value">{item.name}</strong>
                    <p className="card-copy">{renderReference(item.reference)}</p>
                    <p className="comps-source-ref">{`Supports: ${item.sectionsSupported.join(", ")}`}</p>
                  </div>
                </div>
              ))}
            </div>
          </SummaryCard>
        </div>

        <div className={getMotionClass(reducedMotion, "fade-up")} style={getStaggerStyle(16, reducedMotion)}>
          <GlassCard className="stack-sm">
            <span className="card-eyebrow">Parent Surface</span>
            <div className="stack-xs">
              <h3 className="card-title">Return to Market Support before using this page as the main commercial read</h3>
              <p className="card-copy">
                This page is intentionally kept as supporting comp detail. Use it for deeper reference-set review after reading the parent commercial surface.
              </p>
            </div>
            <div className="row-wrap gap-sm overview-go-deeper-actions">
              <NavLink to="/market-support" className="link-button link-button-secondary">Market Support</NavLink>
              <NavLink to="/precedent" className="link-button link-button-secondary">Precedent</NavLink>
              <NavLink to="/concept" className="link-button link-button-secondary">Concept</NavLink>
              <NavLink to="/underwriting" className="link-button link-button-secondary">Underwriting</NavLink>
              <NavLink to="/memo" className="link-button link-button-primary">Memo</NavLink>
            </div>
          </GlassCard>
        </div>
      </div>
    </PageContainer>
  );
}
